import { Component, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import * as _ from 'lodash';
import { lastValueFrom, take } from 'rxjs';
import { CrossSellingAnswerDto } from 'src/app/api/models/crossSellingAnswerDto.model';
import { CrossSellingQuestionVm } from 'src/app/api/models/crossSellingQuestionVm.model';
import { SaveCrossSellingQuestionsCommand } from 'src/app/api/models/saveCrossSellingQuestionsCommand.model';
import { CrossSellingService } from 'src/app/api/services/crossSelling.service';
import { AppResource } from 'src/app/app.resource';
import { BackendContext } from 'src/app/core/models/context.model';
import { Contract, CrossSelling, Devis } from 'src/app/core/models/form-state.model';
import { Stepper } from 'src/app/core/models/stepper.model';
import * as fromContext from 'src/app/core/state/context';
import * as fromContract from 'src/app/core/state/contract';
import { State } from 'src/app/core/state/core.state';
import * as formCrossSelling from 'src/app/core/state/cross-selling';
import * as fromDevis from 'src/app/core/state/devis';
import * as fromInfo from 'src/app/core/state/info';
import { OfferCustomizationState } from 'src/app/core/state/offer-customization';
import * as fromSubscriber from 'src/app/core/state/subscriber';
import * as fromPath from '../../../core/state/path';
import { SubscriberState } from 'src/app/core/state/subscriber';
import { BaseComponent } from 'src/app/shared/components/base-component/base-component.component';
import { CANAL_VENTE_C } from 'src/app/shared/constants/context.constants';
import {
  E_SIGN_MSG_ADVISOR_PATH,
  ELECTRONIC_SIGNATURE_PATH
} from 'src/app/shared/constants/route.constants';
import { HeaderVM } from 'src/app/shared/models/components/headerVm';
import { QuestionVM } from 'src/app/shared/models/components/questionVm';
import { ResponseVM } from 'src/app/shared/models/components/response.model';
import { PolicySummaryService } from 'src/app/shared/services/policy-summary.service';
import { environment } from 'src/environments/environment';
import { isPartner } from 'src/app/shared/utils/brandUtils';
import { PathType } from 'src/app/core/enums/path-type.enum';
import { GtmEventServiceService } from 'src/app/shared/services/gtm-event-service.service';

@Component({
  selector: 'app-cross-selling',
  templateUrl: './cross-selling.component.html',
  styleUrl: './cross-selling.component.scss'
})
export class CrossSellingComponent extends BaseComponent implements OnInit {
  context!: string;
  contextData!: BackendContext | null;
  contrat!: Contract;
  devis!: Devis;
  path!: PathType;
  subscriber!: SubscriberState;
  crossSelling!: CrossSelling;
  offerCustomization!: OfferCustomizationState;
  sansEffetPhoneNumber: string = '';

  isLoading: boolean = false;
  displayError: boolean = false;
  displayErrorText: boolean = false;
  triedSubmit = false;
  customerExperience = false; // Defines if should display the page for customer or for prospect
  hideSubmitButton = false;
  ignoreSubmission = false;

  crossSellingQuestions = new Array<CrossSellingQuestionVm>();
  questions: Array<QuestionVM> | null = null;

  headerStep!: HeaderVM;
  form: FormGroup = new FormGroup({
    marketing_phone_consent: new FormControl<boolean>(false),
    marketing_email_opposition: new FormControl<boolean>(false)
  });

  get brand() {
    return environment.brand;
  }

  constructor(
    store: Store<State>,
    resources: AppResource,
    public router: Router,
    private crossSellingService: CrossSellingService,
    private policySummaryService: PolicySummaryService,
    private gtmEventService: GtmEventServiceService
  ) {
    super(store, resources);

    this.headerStep = new HeaderVM(
      this.resource.header.stepCrossSelling,
      3,
      this.resource.header.subStepCrossSelling,
      10
    );
  }

  override async ngOnInit() {
    await this.loadStateData();

    if (this.contextData?.canalVente === CANAL_VENTE_C) {
      this.customerExperience = false;
    } else {
      this.customerExperience = true;
      console.log(this.contrat);
      if (this.contrat?.EstSigne === false) {
        this.ignoreSubmission = true;
        this.router.navigate([ELECTRONIC_SIGNATURE_PATH]);
        return;
      } else {
        // Do nothing, the agreement SalesChannel will be retrieved with questions and if equals to "C" the questions will not be displayed

        // Log GTM event only for canalVente W and only when displayed after contract signature.
        this.logGtmEvent();
      }
    }

    super.ngOnInit();
    this.getQuestions();
  }

  getQuestions() {
    let agreementId = this.customerExperience
      ? this.contrat.NumeroContrat
      : this.devis.AgreementId?.toString();

    if (_.isNil(agreementId) || agreementId === '') {
      console.error('AgreementId is undefined or null');
      if (this.customerExperience) {
        this.policySummaryService.processPolicySummary(
          this.contrat.NumeroContrat,
          this.context,
          this.subscriber.subscriber?.Email!
        );
      } else {
        this.router.navigate([E_SIGN_MSG_ADVISOR_PATH]);
      }
      return;
    }

    this.crossSellingService
      .getQuestions(agreementId ?? '', this.contextData?.canalVente ?? '')
      .subscribe(information => {
        if (this.customerExperience && information?.salesChannel === CANAL_VENTE_C) {
          console.log('Les questions ont déjà été répondues lors de la souscription.');
          this.ignoreSubmission = true;
          this.policySummaryService.processPolicySummary(
            this.contrat.NumeroContrat,
            this.context,
            this.subscriber.subscriber?.Email!
          );
          return;
        }

        if (_.isNil(information?.questions) || information.questions!.length === 0) {
          console.log('Pas de questions additionnelles à poser');
        }

        this.crossSellingQuestions = information?.questions ?? [];

        this.crossSellingQuestions.forEach(q => {
          this.form.addControl(
            `question-${q.questionId}`,
            new FormControl('', Validators.required)
          );
        });

        this.form.controls['marketing_phone_consent'].setValue(
          information?.isMarketingPhoneConsent ?? false
        );
        this.form.controls['marketing_email_opposition'].setValue(
          !(information?.isMarketingEmailConsent ?? true)
        );

        this.questions = _.orderBy(this.crossSellingQuestions, ['order'], ['asc']).map((q: any) => {
          return new QuestionVM(
            q.questionText!,
            [
              new ResponseVM(
                this.resource.form.yes,
                true,
                !_.isNil(q.answer) && q.answer === true ? true : false
              ),
              new ResponseVM(
                this.resource.form.no,
                false,
                !_.isNil(q.answer) && q.answer === false ? true : false
              )
            ],
            q.questionId
          );
        });
      });
  }

  private async loadStateData() {
    // Retrieve context
    let contextId = await lastValueFrom(
      this.store.select(fromContext.selectContextId).pipe(take(1))
    );
    this.context = contextId!;

    this.contextData = await lastValueFrom(
      this.store.select(fromContext.selectContextData).pipe(take(1))
    );

    // Retrieve contract
    this.contrat = await lastValueFrom(
      this.store.select(fromContract.selectContractState).pipe(take(1))
    );

    // Retrieve devis
    this.devis = await lastValueFrom(this.store.select(fromDevis.selectDevisState).pipe(take(1)));

    // Retrieve subscriber
    this.subscriber = await lastValueFrom(
      this.store.select(fromSubscriber.selectSubscriber).pipe(take(1))
    );

    // Retrieve cross selling
    this.crossSelling = await lastValueFrom(
      this.store.select(formCrossSelling.selectCrossSellingState).pipe(take(1))
    );

    this.path = await lastValueFrom(this.store.select(fromPath.selectPathType).pipe(take(1)));
  }

  async validateQuestions() {
    if (this.ignoreSubmission) {
      this.ignoreSubmission = false;
      this.policySummaryService.processPolicySummary(
        this.contrat.NumeroContrat,
        this.context,
        this.subscriber.subscriber?.Email!
      );
      return;
    }

    if (this.form.invalid) {
      this.triedSubmit = true;
      return;
    }

    let answers: CrossSellingAnswerDto[] = [];
    this.questions?.forEach(q => {
      let answer = this.form.get(`question-${q.id}`)?.value;
      if (!_.isNil(answer)) {
        answers.push({ questionId: q.id, answer: answer.toString() });
      }
    });

    let command: SaveCrossSellingQuestionsCommand = {
      agreementId: this.contrat.NumeroContrat,
      answers: answers,
      marketingPhoneConsent: this.form.get('marketing_phone_consent')?.value,
      marketingEmailConsent: !this.form.get('marketing_email_opposition')?.value
    };
    console.log(command);

    this.crossSellingService.patchMemberAnswers(command).subscribe({
      next: () => {
        this.hideSubmitButton = true;
        if (this.customerExperience) {
          this.policySummaryService.processPolicySummary(
            this.contrat.NumeroContrat,
            this.context,
            this.subscriber.subscriber?.Email!
          );
        }
      },
      error: resSignature => {
        this.isLoading = false;
        const errorDetail = resSignature.error?.detail;
        this.displayError = true;
      },
      complete: () => {
        this.isLoading = false;
        this.displayError = false;
        this.displayErrorText = false;
        this.updateStepper();
      }
    });
  }

  async updateStepper() {
    let stepper = await lastValueFrom(this.store.select(fromInfo.selectStepper).pipe(take(1)));
    let tmpStepper = JSON.parse(JSON.stringify(stepper)) as Stepper;

    let title = tmpStepper.contract.titles.find(
      (x: { libelle: string }) => x.libelle == this.resource.stepper.signature
    );

    let subtitleS = title?.subtitles.find(
      (x: { url: string }) => x.url == this.resource.stepper.crossselling
    );

    if (subtitleS) {
      subtitleS.isValid = true;
      subtitleS.isActive = true;
    }

    let subtitleNS = title?.subtitles.find(
      (x: { url: string }) => x.url == this.resource.stepper.confirmcontract
    );

    if (subtitleNS) {
      subtitleNS.isValid = true;
      subtitleNS.isActive = true;
    }

    this.store.dispatch(fromInfo.updateStepper({ payload: tmpStepper }));
  }

  public isPartner(): boolean {
    return isPartner(environment.brand);
  }

  async logGtmEvent(): Promise<void> {
    // here we use originalCanalVente instead of canalVente, as we need to know how the sale was initiated.
    let isWebChannel = this.contextData?.originalCanalVente !== CANAL_VENTE_C;
    if (isWebChannel === false) return;

    await this.gtmEventService.pushEvent('contract_signature_submit');
  }
}
